import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  History, ChevronDown, ChevronRight, RefreshCw, Loader2, Search,
  ArrowUp, ArrowDown, X,
} from "lucide-react";
import { retryOnReloadHiccup } from "../utils/retryOnReloadHiccup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-08-03 — Audit Trail screen (Platform Admin only). Consumes the
// existing GET /api/admin/audit/recent endpoint. Purpose: let Dave verify
// mutations from the UI without dropping into a SQL console.

const ENTITY_TYPES = [
  "",                 // "All"
  "firm",
  "association",
  "plan_year",
  "scenario",
  "element",
  "membership",
  "user",
  "platform_setting",
  "platform",
  "email",
  "faq",
  "glossary",
];
// "plan_year" → "Plan Year"
const entityTypeLabel = (t) => (t ? t.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") : "(All)");

const LIMITS = [25, 50, 100, 250, 500, 1000];

const DATE_RANGES = [
  ["all", "All time"], ["today", "Today"], ["7d", "Last 7 days"],
  ["30d", "Last 30 days"], ["year", "This year"], ["custom", "Custom…"],
];

// Local-time `since` / `until` (until is exclusive) for the chosen date range.
function dateParams(range, from, to) {
  const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const back = (n) => { const d = dayStart(now); d.setDate(d.getDate() - n); return d; };
  const p = {};
  if (range === "today") p.since = dayStart(now);
  else if (range === "7d") p.since = back(6);
  else if (range === "30d") p.since = back(29);
  else if (range === "year") p.since = new Date(now.getFullYear(), 0, 1);
  else if (range === "custom") {
    if (from) p.since = new Date(`${from}T00:00:00`);
    if (to) { const u = new Date(`${to}T00:00:00`); u.setDate(u.getDate() + 1); p.until = u; }
  }
  return Object.fromEntries(Object.entries(p).map(([k, v]) => [k, v.toISOString()]));
}

const selCls = "border border-rule rounded-[3px] px-2 py-1 text-[12.5px] focus:outline-none focus:border-navy";
const lblCls = "text-[10px] tracking-[0.1em] uppercase font-semibold text-slate";

// Second filter row: Date range, Firm, Association (narrowed to the picked Firm).
function AuditScopeFilters({ firms, associations, firmId, setFirmId, assocId, setAssocId, dateRange, setDateRange, fromDate, setFromDate, toDate, setToDate, onClear, canClear }) {
  const assocList = associations
    .filter((a) => !firmId || a.tenant_id === firmId)
    .sort((a, b) => Number(a.tenant_display_id || 0) - Number(b.tenant_display_id || 0));
  return (
    <div className="flex items-end gap-2 bg-white border border-rule rounded-[8px] shadow-sm p-2 -mt-1.5" data-testid="audit-scope-filters">
      <label className="flex flex-col gap-0.5">
        <span className={lblCls}>Date</span>
        <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className={selCls} data-testid="audit-filter-date">
          {DATE_RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      {dateRange === "custom" && (
        <>
          <label className="flex flex-col gap-0.5">
            <span className={lblCls}>From</span>
            <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className={selCls} data-testid="audit-filter-from" />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className={lblCls}>To</span>
            <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className={selCls} data-testid="audit-filter-to" />
          </label>
        </>
      )}
      <label className="flex flex-col gap-0.5">
        <span className={lblCls}>Firm</span>
        <select value={firmId} onChange={(e) => setFirmId(e.target.value)} className={selCls + " max-w-[240px]"} data-testid="audit-filter-firm">
          <option value="">(All)</option>
          {[...firms].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((f) => (
            <option key={f.tenant_id} value={f.tenant_id}>{f.name}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-0.5 flex-1">
        <span className={lblCls}>Association</span>
        <select value={assocId} onChange={(e) => setAssocId(e.target.value)} className={selCls} data-testid="audit-filter-association">
          <option value="">(All)</option>
          {assocList.map((a) => (
            <option key={a.aid} value={a.aid}>{a.tenant_display_id ? `${a.tenant_display_id} · ` : ""}{a.name}</option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={onClear}
        disabled={!canClear}
        className="px-3 py-1 text-[12px] font-semibold border border-navy text-navy rounded-[3px] hover:bg-navy hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-navy transition-colors flex items-center gap-1.5"
        data-testid="audit-clear-filters"
      >
        <X size={13} /> Clear Filters
      </button>
    </div>
  );
}

const ACTION_STYLE = {
  create:     { bg: "#EAF6EF", fg: "#2E7D5B", border: "#2E7D5B" },
  update:     { bg: "#E9F1F9", fg: "#1C4E80", border: "#1C4E80" },
  delete:     { bg: "#FEF5F2", fg: "#EA6A47", border: "#EA6A47" },
  deactivate: { bg: "#FDF6E3", fg: "#9C6B00", border: "#9C6B00" },
  reactivate: { bg: "#EAF6EF", fg: "#2E7D5B", border: "#2E7D5B" },
  purge:      { bg: "#FEE2DA", fg: "#B4331A", border: "#B4331A" },
};

function ActionBadge({ action, wide = false }) {
  const s = ACTION_STYLE[action] || { bg: "#F1F3F5", fg: "#5A6B75", border: "#B8C1CB" };
  return (
    <span
      className="inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] tracking-[0.1em] uppercase font-bold rounded-[3px]"
      // 2026-02-19 (Dave) — `wide` enforces a uniform width across all
      // action variants inside the Action COLUMN of the table so the
      // badges align to a single right edge. Summary chips (top of
      // page) still use the tight variant so the row of "UPDATE ×28
      // SOFT_DELETE ×3 …" chips stays compact.
      //
      // 152px is the tightest fit for "CHANGE_OWN_PASSWORD" (the
      // longest current action label, 19 chars · 0.1em tracking) plus
      // horizontal padding. If a future action label exceeds 19 chars,
      // bump this value.
      style={{
        background: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
        ...(wide ? { minWidth: 152 } : {}),
      }}
    >
      {action}
    </span>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function JsonPanel({ label, data, testid }) {
  if (data === null || data === undefined) {
    return (
      <div className="flex-1 min-w-0" data-testid={testid}>
        <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-slate mb-1">{label}</div>
        <div className="text-[11px] text-slate italic border border-rule bg-navy-wash/30 px-2 py-3 rounded-[3px]">
          (none)
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 min-w-0" data-testid={testid}>
      <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-slate mb-1">{label}</div>
      <pre className="text-[11px] leading-relaxed text-ink border border-rule bg-navy-wash/30 px-2 py-2 rounded-[3px] overflow-x-auto font-mono whitespace-pre-wrap break-words max-h-72 overflow-y-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function AuditRow({ event }) {
  const [open, setOpen] = useState(false);
  const shortEntityId = event.entity_id ? event.entity_id.slice(0, 8) : "";
  return (
    <>
      <tr
        className="border-b border-rule hover:bg-navy-wash/40 cursor-pointer"
        onClick={() => setOpen((o) => !o)}
        data-testid={`audit-row-${event.event_id}`}
      >
        <td className="px-3 py-1 text-slate">
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </td>
        <td className="px-3 py-1 text-[11.5px] text-ink font-mono whitespace-nowrap">
          {formatWhen(event.at)}
        </td>
        <td className="px-3 py-1 text-[12px] text-ink">
          <div className="font-semibold">{event.actor_name || "—"}</div>
          {event.actor_email && (
            <div className="text-[10.5px] text-slate">{event.actor_email}</div>
          )}
        </td>
        <td className="px-3 py-1">
          <ActionBadge action={event.action} wide />
        </td>
        <td className="px-3 py-1 text-[12px] text-ink">
          <div className="font-semibold">{event.entity_type}</div>
          <div className="text-[10px] text-slate font-mono" title={event.entity_id}>{shortEntityId}…</div>
        </td>
        <td className="px-3 py-1 text-[12px] text-ink">
          {event.summary || <span className="text-slate italic">—</span>}
        </td>
      </tr>
      {open && (
        <tr className="border-b-2 border-navy bg-navy-wash/20" data-testid={`audit-detail-${event.event_id}`}>
          <td />
          <td colSpan={5} className="px-3 pb-4 pt-2">
            <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-slate mb-2">
              Event ID · <span className="font-mono normal-case tracking-normal text-ink">{event.event_id}</span>
              {event.tenant_id && (
                <span className="ml-4">Tenant · <span className="font-mono normal-case tracking-normal text-ink">{event.tenant_name || event.tenant_id.slice(0, 8) + "…"}</span></span>
              )}
              <span className="ml-4">Entity · <span className="font-mono normal-case tracking-normal text-ink">{event.entity_id}</span></span>
            </div>
            <div className="flex gap-4">
              <JsonPanel label="Before" data={event.before_json} testid={`audit-before-${event.event_id}`} />
              <JsonPanel label="After" data={event.after_json} testid={`audit-after-${event.event_id}`} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AuditTrailScreen() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [entityType, setEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [userQ, setUserQ] = useState("");
  const [firms, setFirms] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [firmId, setFirmId] = useState("");
  const [assocId, setAssocId] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [resetTick, setResetTick] = useState(0);
  const clearFilters = () => {
    setEntityType(""); setEntityId(""); setUserQ(""); setFirmId(""); setAssocId("");
    setDateRange("all"); setFromDate(""); setToDate("");
    setResetTick((n) => n + 1);
  };

  useEffect(() => {
    axios.get(`${API}/admin/firms`).then((r) => setFirms(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/associations`).then((r) => setAssociations(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const [limit, setLimit] = useState(50);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = { limit };
      if (entityType) params.entity_type = entityType;
      if (entityId.trim()) params.entity_id = entityId.trim();
      if (userQ.trim()) params.user = userQ.trim();
      if (firmId) params.firm_id = firmId;
      if (assocId) params.association_id = assocId;
      Object.assign(params, dateParams(dateRange, fromDate, toDate));
      const res = await retryOnReloadHiccup(() =>
        axios.get(`${API}/admin/audit/recent`, { params }),
      );
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load audit events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [limit, entityType, firmId, assocId, dateRange, fromDate, toDate, resetTick]);

  const grouped = useMemo(() => {
    const counts = {};
    for (const e of events) counts[e.action] = (counts[e.action] || 0) + 1;
    return counts;
  }, [events]);

  // 2026-08-18 (Dave) — client-side sort on When, User, or Action.
  // Default: When descending (newest first — matches the endpoint's natural
  // ordering). Clicking the same header toggles direction; clicking a
  // different one resets to descending for that column.
  //
  // Secondary sort rule (Dave 2026-08-18b): whenever the primary sort is
  // NOT "when", we tie-break by When descending — so within a User group
  // or an Action group the newest event is always on top.
  const [sortKey, setSortKey] = useState("when");    // "when" | "user" | "action"
  const [sortDir, setSortDir] = useState("desc");    // "asc"  | "desc"
  const changeSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortedEvents = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const whenOf = (r) => (r.at ? new Date(r.at).getTime() : 0);
    const cmp = (a, b) => {
      if (sortKey === "when") {
        return (whenOf(a) - whenOf(b)) * dir;
      }
      // Primary: user or action label.
      let primary;
      if (sortKey === "user") {
        const ua = (a.actor_name || a.actor_email || "").toLowerCase();
        const ub = (b.actor_name || b.actor_email || "").toLowerCase();
        primary = ua.localeCompare(ub) * dir;
      } else {
        // sortKey === "action"
        const aa = (a.action || "").toLowerCase();
        const bb = (b.action || "").toLowerCase();
        primary = aa.localeCompare(bb) * dir;
      }
      if (primary !== 0) return primary;
      // Secondary: newest first, regardless of the primary direction.
      return whenOf(b) - whenOf(a);
    };
    return [...events].sort(cmp);
  }, [events, sortKey, sortDir]);

  const SortableTh = ({ label, sortField, testid }) => (
    <th className="px-3 py-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold text-navy">
      <button
        type="button"
        onClick={() => changeSort(sortField)}
        className="inline-flex items-center gap-1 text-navy hover:opacity-80"
        data-testid={testid}
      >
        {label}
        {sortKey === sortField ? (
          sortDir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />
        ) : (
          <ArrowDown size={11} className="opacity-30" />
        )}
      </button>
    </th>
  );

  return (
    // 2026-08-18 (Dave, follow-up) — outer switched from `overflow-auto`
    // to `overflow-hidden flex flex-col` so the sticky thead inside the
    // table's own scroll container is the ONLY thing that scrolls. The
    // page itself doesn't scroll any more — that's what makes the header
    // block + search bar stay pinned above the table.
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="screen-audit">
      <div className="max-w-6xl mx-auto p-6 flex flex-col gap-3 flex-1 min-h-0 w-full">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 pb-2 mb-1" style={{ borderBottom: "2px solid #1C4E80" }}>
            <History size={18} className="text-navy" />
            <h1 className="text-[18px] font-bold text-navy tracking-tight">
              Audit Trail
            </h1>
            <span className="ml-2 text-[10px] tracking-[0.12em] uppercase font-semibold text-slate">
              Platform Admin
            </span>
          </div>
          <p className="text-[12.5px] text-slate">
            Immutable ledger of every mutation across the platform: who, what, before, after, when.
            Ordered newest first.
          </p>
        </div>

        {/* Filter bar — compacted 2026-08-18 (Dave): tighter padding
            and control heights so the column-heading row rides higher. */}
        <div className="flex items-end gap-2 bg-white border border-rule rounded-[8px] shadow-sm p-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] tracking-[0.1em] uppercase font-semibold text-slate">Entity Type</span>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="border border-rule rounded-[3px] px-2 py-1 text-[12.5px] focus:outline-none focus:border-navy"
              data-testid="audit-filter-type"
            >
              {ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>{entityTypeLabel(t)}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-0.5 flex-1">
            <span className="text-[10px] tracking-[0.1em] uppercase font-semibold text-slate">Entity ID or Association #</span>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate" />
              <input
                type="text"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Association number (e.g. 108) or paste an exact ID"
                className="w-full border border-rule rounded-[3px] pl-7 pr-2 py-1 text-[12.5px] font-mono focus:outline-none focus:border-navy"
                data-testid="audit-filter-id"
              />
            </div>
          </label>
          <label className="flex flex-col gap-0.5 w-56">
            <span className="text-[10px] tracking-[0.1em] uppercase font-semibold text-slate">User</span>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate" />
              <input
                type="text"
                value={userQ}
                onChange={(e) => setUserQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Name or email"
                className="w-full border border-rule rounded-[3px] pl-7 pr-2 py-1 text-[12.5px] focus:outline-none focus:border-navy"
                data-testid="audit-filter-user"
              />
            </div>
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] tracking-[0.1em] uppercase font-semibold text-slate">Limit</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="border border-rule rounded-[3px] px-2 py-1 text-[12.5px] focus:outline-none focus:border-navy"
              data-testid="audit-filter-limit"
            >
              {LIMITS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-3 py-1 text-[12px] font-semibold bg-navy text-white rounded-[3px] hover:bg-[#12406B] disabled:opacity-40 flex items-center gap-1.5"
            data-testid="audit-refresh"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Refresh
          </button>
        </div>
        <AuditScopeFilters
          firms={firms}
          associations={associations}
          firmId={firmId}
          setFirmId={(v) => { setFirmId(v); setAssocId(""); }}
          assocId={assocId}
          setAssocId={setAssocId}
          dateRange={dateRange}
          setDateRange={setDateRange}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          onClear={clearFilters}
          canClear={!!(entityType || entityId || userQ || firmId || assocId || dateRange !== "all")}
        />

        {/* Summary chips */}
        {!loading && events.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="text-slate">Showing {events.length}:</span>
            {Object.entries(grouped).map(([action, n]) => (
              <span key={action}>
                <ActionBadge action={action} /> <span className="text-slate ml-0.5">×{n}</span>
              </span>
            ))}
          </div>
        )}

        {/* Table — 2026-08-18 (Dave):
              • Row heights compacted: td/th py-2 → py-1 so twice as many
                events fit on one screen.
              • Column-heading row is `sticky top-0` inside its own scroll
                container so it stays pinned as the body scrolls.  The
                page itself no longer scrolls the table; the table body
                does. `max-h` uses viewport minus a safe estimate of the
                blocks above so the table always takes whatever's left. */}
        <div
          className="bg-white border border-rule rounded-[8px] shadow-sm overflow-hidden flex-1 min-h-0"
        >
          <div className="h-full overflow-y-auto">
          <table className="w-full">
            <thead
              className="bg-navy-wash sticky top-0 z-10 border-b-2 border-navy"
              style={{ boxShadow: "0 2px 0 #1C4E80" }}
            >
              <tr>
                <th className="w-6" />
                <SortableTh label="When" sortField="when" testid="audit-sort-when" />
                <SortableTh label="User" sortField="user" testid="audit-sort-user" />
                <SortableTh label="Action" sortField="action" testid="audit-sort-action" />
                <th className="px-3 py-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold text-navy">Entity</th>
                <th className="px-3 py-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold text-navy">Summary</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[12px] text-slate">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Loading events…
                </td></tr>
              )}
              {!loading && err && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[12px] text-coral" data-testid="audit-error">{err}</td></tr>
              )}
              {!loading && !err && events.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[12px] text-slate italic">
                  No events match the current filters.
                </td></tr>
              )}
              {!loading && !err && sortedEvents.map((ev) => (
                <AuditRow key={ev.event_id} event={ev} />
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
}
